import { type FormEvent, useState } from "react";
import { ApiError, api } from "./api";
import { PageHeader } from "./layout/Layout";
import { TwoFactor } from "./TwoFactor";
import { AuthenticationLayout } from "./SignIn";
import { Alert, Box, Working } from "./ui";

/**
 * Changing a password.
 *
 * Forced, it stands alone in the template's authentication frame, because everyone starts
 * on a temporary password somebody handed them. Chosen from the profile menu, it is an
 * ordinary page inside the app.
 */
export function ChangePassword({
  forced,
  onDone,
}: {
  readonly forced: boolean;
  readonly onDone: () => void;
}): React.ReactElement {
  const form = <PasswordForm onDone={onDone} forced={forced} />;

  if (forced) {
    return (
      <AuthenticationLayout
        title="Set your password"
        subtitle="You are signed in with a temporary password. Choose a personal password before continuing."
      >
        {form}
      </AuthenticationLayout>
    );
  }

  return (
    <>
      <PageHeader title="Sign-in settings" subtitle="Change your password and set up an authenticator app." />
      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-5 lg:col-span-7 col-span-12">
          <Box title="Change password">{form}</Box>
        </div>
        <div className="xl:col-span-7 lg:col-span-5 col-span-12">
          <TwoFactor />
        </div>
      </div>
    </>
  );
}

function PasswordForm({ onDone, forced }: { readonly onDone: () => void; readonly forced: boolean }): React.ReactElement {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    if (next !== again) {
      setError("The new passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      if (forced) {
        onDone();
        return;
      }
      setSaved(true);
      setCurrent("");
      setNext("");
      setAgain("");
      setBusy(false);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong");
      setBusy(false);
    }
  }

  return (
    <form className="grid grid-cols-12 gap-y-4" onSubmit={(event) => void submit(event)}>
      {error ? (
        <div className="col-span-12">
          <Alert tone="danger">{error}</Alert>
        </div>
      ) : null}
      {saved ? (
        <div className="col-span-12">
          <Alert tone="success">Password changed. Your other sessions have been signed out.</Alert>
        </div>
      ) : null}
      <div className="col-span-12">
        <label htmlFor="current-password" className="form-label text-default">
          Current password
        </label>
        <input
          id="current-password"
          type="password"
          className="form-control form-control-lg w-full !rounded-md"
          value={current}
          autoComplete="current-password"
          onChange={(event) => setCurrent(event.target.value)}
        />
      </div>
      <div className="col-span-12">
        <label htmlFor="new-password" className="form-label text-default">
          New password
        </label>
        <input
          id="new-password"
          type="password"
          className="form-control form-control-lg w-full !rounded-md"
          value={next}
          autoComplete="new-password"
          onChange={(event) => setNext(event.target.value)}
        />
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">
          At least 10 characters. A short sentence is easier to remember than a word with symbols in it.
        </p>
      </div>
      <div className="col-span-12">
        <label htmlFor="again-password" className="form-label text-default">
          Confirm new password
        </label>
        <input
          id="again-password"
          type="password"
          className="form-control form-control-lg w-full !rounded-md"
          value={again}
          autoComplete="new-password"
          onChange={(event) => setAgain(event.target.value)}
        />
      </div>
      <div className="col-span-12 grid mt-2">
        <button type="submit" className="ti-btn ti-btn-primary !bg-primary !text-white !font-medium" disabled={busy}>
          {busy ? <Working label="Saving" /> : "Save password"}
        </button>
      </div>
    </form>
  );
}
